import * as qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import Button, { EButtonColor } from '../../../components/UI/Button/Button';
import { EInputType, IInputField, IOption } from '../../../components/UI/Input/Input';
import { useInputs } from '../../../components/UI/Input/useInputs';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { IAppState, IFeedback, IFeedbackState } from '../../../interfaces';
import { createOptions } from '../../../shared/option-utils';

import * as actions from '../../../store/actions';
import { ETranslation } from '../../../translations/translation-keys';

interface IProps extends Partial<RouteComponentProps> {
  offerRequestId?: string;
  pinCode?: string;
  authAccessKey?: string;
  embedded?: boolean;
}

export enum EFeedback {
  serviceProvider = "serviceProvider",
  serviceProviderOther = "serviceProviderOther",
  serviceSatisfaction = "serviceSatisfaction",
  operatorToken = "operatorToken",
  serviceToken = "serviceToken",
  message = "message",
  realizedPrice = "realizedPrice",
  offerCountSatisfaction = "offerCountSatisfaction",
  trustScore = "trustScore",
}

const Feedback: React.FC<IProps> = ({ history, location, offerRequestId, pinCode, authAccessKey, embedded }) => {
  const [id, setId] = useState("");
  const [pin, setPin] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [step, setStep] = useState<'rate' | 'closed_prompt' | 'detailed' | 'finished'>('rate');

  const [serviceProviderOptions, setServiceProviderOptions] = useState<
    IOption[]
  >([]);

  useEffect(() => {
    if (offerRequestId) {
      setId(offerRequestId);
      setPin(pinCode || "");
      setAccessKey(authAccessKey || "");
    } else if (location?.search) {
      const q = qs.parse(location.search);
      setId(q.id as string);
      setPin(q.pin as string);
      setAccessKey(q.accessKey as string);
    }
  }, [location, offerRequestId, pinCode, authAccessKey]);

  const { loading, serviceProviders, offerRequest, error } = useSelector<IAppState, IFeedbackState>(state => state.feedbacks);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [inputs, setInputs] = useState<IInputField>({
    [EFeedback.serviceSatisfaction]: {
      type: EInputType.star,
      labelTranslation: ETranslation.FEEDBACK_SATISFACTION,
      placeholderTranslation: ETranslation.FEEDBACK_SATISFACTION,
      value: "",
      options: [
        { value: "1" },
        { value: "2" },
        { value: "3" },
        { value: "4" },
        { value: "5" },
      ],
      validation: {
        required: true,
      },
    },
    [EFeedback.serviceProvider]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.FEEDBACK_SERVICEPROVIDER,
      placeholder: "Valitse palveluntarjoaja",
      value: "",
      options: [],
      validation: {
        required: true,
      },
    },
    [EFeedback.serviceProviderOther]: {
      type: EInputType.text,
      labelTranslation: ETranslation.FEEDBACK_SERVICE_PROVIDER_OTHER,
      placeholderTranslation: ETranslation.FEEDBACK_SERVICE_PROVIDER_OTHER,
      value: "",
    },
    [EFeedback.realizedPrice]: {
      type: EInputType.text,
      labelTranslation: ETranslation.FEEDBACK_REALIZED_PRICE,
      placeholder: "esim. 1200 €",
      info: "Hinta on meille tärkeää tietoa, jotta saamme realistisen kuvan alan hinnoittelusta. Arvio riittää.",
      value: "",
      validation: {
        required: true,
      },
    },
    [EFeedback.offerCountSatisfaction]: {
      type: EInputType.radio,
      labelTranslation: ETranslation.FEEDBACK_OFFER_COUNT_SATISFACTION,
      value: "",
      options: [
        { value: "Kyllä, yhden", label: "Kyllä, yhden" },
        { value: "Kyllä, useita", label: "Kyllä, useita" },
        { value: "En saanut yhtään", label: "En saanut yhtään" },
        { value: "En saanut tarpeeksi", label: "En saanut tarpeeksi" },
      ],
      validation: {
        required: true,
      },
    },
    [EFeedback.trustScore]: {
      type: EInputType.star,
      labelTranslation: ETranslation.FEEDBACK_TRUST_SCORE,
      placeholderTranslation: ETranslation.FEEDBACK_TRUST_SCORE,
      value: "",
      options: [
        { value: "1" },
        { value: "2" },
        { value: "3" },
        { value: "4" },
        { value: "5" },
      ],
      validation: {
        required: true,
      },
    },
    [EFeedback.serviceToken]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_TOKEN,
      value: "",
    },
    [EFeedback.message]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.FEEDBACK_MESSAGE,
      placeholderTranslation: ETranslation.FEEDBACK_MESSAGE,
      value: "",
      rows: 5,
    },
  });

  // get offer request
  useEffect(() => {
    if (accessKey || (id && pin)) {
      dispatch(actions.getFeedbackFormData(id, pin, accessKey));
    }
  }, [dispatch, id, pin, accessKey]);

  useEffect(() => {
    if (offerRequest?.feedbackGiven) {
      setStep('finished');
    } else if (offerRequest?.feedbackId && step === 'rate') {
      setStep('closed_prompt');
    }
  }, [offerRequest?.feedbackGiven, offerRequest?.feedbackId, step]);

  // put service providers into input
  useEffect(() => {
    if (serviceProviders) {
      const sortedProviders = [...serviceProviders].sort((a, b) => 
        (a.name || "").localeCompare(b.name || "", 'fi', { sensitivity: 'base' })
      );
      const options = createOptions(sortedProviders, (s) => s.name || "");
      options.unshift({
        value: ETranslation.FEEDBACK_FORM_NOT_FOUND,
        label: t(ETranslation.FEEDBACK_FORM_NOT_FOUND),
      });
      options.push({
        value: ETranslation.FEEDBACK_FORM_SOMETHING_ELSE,
        label: t(ETranslation.FEEDBACK_FORM_SOMETHING_ELSE),
      });
      setServiceProviderOptions(options);
    }
  }, [serviceProviders, t]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
  });

  const serviceSatisfaction = inputs[EFeedback.serviceSatisfaction].value as string;
  const serviceProvider = inputs[EFeedback.serviceProvider].value as string;
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Quick submit: only rating
  const quickSubmitHandler = async (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (!serviceSatisfaction) return;

    const feedback = {
      serviceSatisfaction: Number(serviceSatisfaction),
      source: "customer",
      isDetailed: false,
    } as IFeedback;

    dispatch(actions.saveFeedback(feedback, id, pin, accessKey));
    setStep('closed_prompt');
  };

  // Detailed submit: all filled fields
  const detailedSubmitHandler = async (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setSubmitError(null);

    const data = await submit();
    if (!data) {
      const missing: string[] = [];
      if (!inputs[EFeedback.serviceProvider]?.value) {
        missing.push(t(ETranslation.FEEDBACK_SERVICEPROVIDER));
      }
      if (!inputs[EFeedback.realizedPrice]?.value) {
        missing.push(t(ETranslation.FEEDBACK_REALIZED_PRICE));
      }
      if (!inputs[EFeedback.offerCountSatisfaction]?.value) {
        missing.push(t(ETranslation.FEEDBACK_OFFER_COUNT_SATISFACTION));
      }
      if (!inputs[EFeedback.trustScore]?.value) {
        missing.push(t(ETranslation.FEEDBACK_TRUST_SCORE));
      }
      if (missing.length > 0) {
        setSubmitError(`Täytäthän seuraavat pakolliset kentät:\n• ${missing.join('\n• ')}`);
      } else {
        setSubmitError("Täytäthän kaikki tähdellä (*) merkityt pakolliset kentät ennen lähettämistä.");
      }
      return;
    }
    const feedback = data as IFeedback;

    feedback.serviceSatisfaction = Number(serviceSatisfaction || 5);
    feedback.source = "customer";
    feedback.isDetailed = true;

    const selectedProvider = inputs[EFeedback.serviceProvider].value as string;
    if (selectedProvider) {
      switch (selectedProvider) {
        case ETranslation.FEEDBACK_FORM_NOT_FOUND:
          feedback.isServiceProviderNotFound = true;
          feedback.serviceProvider = undefined;
          break;
        case ETranslation.FEEDBACK_FORM_SOMETHING_ELSE:
          feedback.isServiceProviderOther = true;
          feedback.serviceProvider = undefined;
          break;
      }
    }

    if (inputs[EFeedback.realizedPrice]?.value) {
      feedback.realizedPrice = String(inputs[EFeedback.realizedPrice].value);
    }
    if (inputs[EFeedback.offerCountSatisfaction]?.value) {
      feedback.offerCountSatisfaction = String(inputs[EFeedback.offerCountSatisfaction].value);
    }
    if (inputs[EFeedback.trustScore]?.value) {
      feedback.trustScore = Number(inputs[EFeedback.trustScore].value);
    }

    dispatch(actions.saveFeedback(feedback, id, pin, accessKey));
    setStep('finished');
  };

  if (offerRequest?.feedbackGiven) {
    return (
      <div style={{ padding: '25px', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: '8px', color: '#0f5132', textAlign: 'center', margin: '20px 0' }}>
        <h3 style={{ marginBottom: '10px', color: '#0f5132' }}>✓ {t(ETranslation.FEEDBACK_RECEIVED_TITLE)}</h3>
        <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.5' }}>
          {t(ETranslation.FEEDBACK_ALREADY_GIVEN_DESC)}
        </p>
      </div>
    );
  }

  if (step === 'finished') {
    return (
      <div style={{ padding: '25px', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: '8px', color: '#0f5132', textAlign: 'center', margin: '20px 0' }}>
        <h3 style={{ marginBottom: '10px', color: '#0f5132' }}>✓ {t(ETranslation.FEEDBACK_RECEIVED_TITLE)}</h3>
        <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.5' }}>
          {t(ETranslation.FEEDBACK_RECEIVED_DESC)}
        </p>
      </div>
    );
  }

  if (error) {
    if (error === "FEEDBACK_ALREADY_EXISTS") {
      return (
        <div style={{ padding: '25px', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: '8px', color: '#0f5132', textAlign: 'center', margin: '20px 0' }}>
          <h3 style={{ marginBottom: '10px', color: '#0f5132' }}>✓ {t(ETranslation.FEEDBACK_RECEIVED_TITLE)}</h3>
          <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.5' }}>
            {t(ETranslation.FEEDBACK_ALREADY_GIVEN_DESC)}
          </p>
        </div>
      );
    }

    return (
      <div style={{ padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: '8px', color: '#842029', margin: '15px 0' }}>
        {t(error)}
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "Container"} style={!embedded ? { maxWidth: '720px', margin: '2rem auto', padding: '0 15px' } : undefined}>
      {!embedded && (
        <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #e9ecef', paddingBottom: '1rem' }}>
          <h2 style={{ margin: '0 0 0.5rem', color: '#2c3e50' }}>{t(ETranslation.TITLE_FEEDBACKS)}</h2>
          {offerRequest && (
            <div style={{ fontSize: '0.95rem', color: '#6c757d' }}>
              {offerRequest.service?.name && <span>{offerRequest.service.name} • </span>}
              {offerRequest.city && <span>{offerRequest.city}</span>}
            </div>
          )}
          {(accessKey || (id && pin)) && !offerRequest?.archived && (
            <div style={{ marginTop: '0.5rem' }}>
              <a 
                href={accessKey ? `/offerrequest?accessKey=${accessKey}` : `/offerrequest?id=${id}&pin=${pin}`}
                style={{ color: '#0d6efd', textDecoration: 'underline', fontSize: '0.9rem' }}
              >
                {t(ETranslation.FEEDBACK_VIEW_OFFER_REQUEST)}
              </a>
            </div>
          )}
        </div>
      )}
      {loading ? (
        <Spinner />
      ) : step === 'closed_prompt' ? (
        <div style={{ padding: '25px', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: '8px', color: '#0f5132', textAlign: 'center', margin: '20px 0' }}>
          <h3 style={{ marginBottom: '10px', color: '#0f5132' }}>✓ {t(ETranslation.FEEDBACK_CLOSED_TITLE)}</h3>
          <p style={{ margin: '0 0 20px 0', fontSize: '1.05rem', lineHeight: '1.5' }}>
            {t(ETranslation.FEEDBACK_PROMPT_VALUABLE)}
          </p>
          <Button
            type="button"
            color={EButtonColor.PRIMARY}
            onClick={() => setStep('detailed')}
            style={{
              padding: '12px 24px',
              fontSize: '1rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>💬</span> {t(ETranslation.FEEDBACK_GIVE_BUTTON)}
          </Button>
        </div>
      ) : step === 'detailed' ? (
        <div>
          <div style={{ padding: '20px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '8px', color: '#1b5e20', marginBottom: '20px', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem' }}>✓ {t(ETranslation.FEEDBACK_CLOSED_TITLE)}</h3>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>
              {t(ETranslation.FEEDBACK_PROMPT_VALUABLE)}
            </p>
          </div>
          <form onSubmit={detailedSubmitHandler}>
            <div style={{ padding: '20px', backgroundColor: '#fcfcfc', border: '1px solid #e9ecef', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '1.25rem', color: '#333' }}>
                {t(ETranslation.FEEDBACK_GIVE_BUTTON)}
              </h4>
              <div style={{ marginBottom: '1.75rem' }}>
                {createInput(EFeedback.serviceProvider, {
                  options: serviceProviderOptions,
                  loading,
                })}
              </div>
              {serviceProvider === ETranslation.FEEDBACK_FORM_SOMETHING_ELSE && (
                <div style={{ marginBottom: '1.75rem' }}>
                  {createInput(EFeedback.serviceProviderOther)}
                </div>
              )}
              <div style={{ marginBottom: '1.75rem' }}>
                {createInput(EFeedback.realizedPrice)}
              </div>
              <div style={{ marginBottom: '1.75rem' }}>
                {createInput(EFeedback.offerCountSatisfaction)}
              </div>
              <div style={{ marginBottom: '1.75rem' }}>
                {createInput(EFeedback.trustScore)}
              </div>
              <div style={{ marginBottom: '1.75rem' }}>
                {createInput(EFeedback.message)}
              </div>

              {submitError && (
                <div
                  style={{
                    padding: '14px 18px',
                    backgroundColor: '#fff3cd',
                    border: '1.5px solid #ffe69c',
                    borderRadius: '8px',
                    color: '#664d03',
                    marginBottom: '1.25rem',
                    fontSize: '0.95rem',
                    lineHeight: '1.5',
                  }}
                >
                  <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span> Täytäthän vaaditut kentät ennen lähettämistä:
                  </div>
                  <div style={{ whiteSpace: 'pre-line', paddingLeft: '8px' }}>
                    {submitError.includes('•') ? submitError.substring(submitError.indexOf('•')) : submitError}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '1.5rem' }}>
                <Button
                  type="submit"
                  color={EButtonColor.PRIMARY}
                  disabled={loading}
                  onClick={detailedSubmitHandler}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {t(ETranslation.COMMON_SEND)}
                </Button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <form onSubmit={quickSubmitHandler}>
          {createInput(EFeedback.serviceSatisfaction)}

          <div style={{ marginTop: '1.25rem', marginBottom: '1.25rem' }}>
            <Button
              type="button"
              color={serviceSatisfaction ? EButtonColor.SUCCESS : EButtonColor.NONE}
              disabled={!serviceSatisfaction || loading}
              onClick={serviceSatisfaction ? quickSubmitHandler : (e) => e.preventDefault()}
              style={{
                width: '100%',
                padding: '12px 20px',
                fontSize: '1rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease-in-out',
                ...(serviceSatisfaction ? {
                  backgroundColor: 'var(--brand-primary, #198754)',
                  color: '#ffffff',
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                } : {
                  backgroundColor: '#f1f5f9',
                  color: '#94a3b8',
                  border: '1px solid #cbd5e1',
                  cursor: 'not-allowed',
                  opacity: 0.85,
                }),
              }}
            >
              {serviceSatisfaction && <span>✓</span>} {t(ETranslation.FEEDBACK_CLOSE_REQUEST)}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Feedback;
